import { useState, useRef, useCallback, useEffect } from 'react';
import { Frame, Plus, Trash2, Check, Edit3, Copy, ExternalLink, QrCode, ChevronLeft, ChevronRight, Upload, ImageIcon, Loader2, Zap, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import QRCodeModal from '../../QRCodeModal';

const FramesPanel = ({
  gallery,
  galleryId,
  photos,
  axiosConfig,
  backendUrl,
  onRefreshGallery
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingFrame, setEditingFrame] = useState(null);
  const [frameName, setFrameName] = useState('');
  const [landscapeFile, setLandscapeFile] = useState(null);
  const [portraitFile, setPortraitFile] = useState(null);
  const [landscapePreview, setLandscapePreview] = useState(null);
  const [portraitPreview, setPortraitPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [activating, setActivating] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [previewPhotoIndex, setPreviewPhotoIndex] = useState(0);
  const [photoOrientations, setPhotoOrientations] = useState({});
  const [rendering, setRendering] = useState(false);
  const [renderStatus, setRenderStatus] = useState(null);
  const [deletingRenders, setDeletingRenders] = useState(false);
  const pollRef = useRef(null);

  const landscapeInputRef = useRef(null);
  const portraitInputRef = useRef(null);

  const frames = gallery?.frames || [];
  const activeFrame = frames.find(f => f.is_active);
  const brandedToken = gallery?.branded_share_token;
  const brandedUrl = brandedToken ? `${window.location.origin}/branded/${brandedToken}` : null;
  const collections = gallery?.collections || [];
  const collectionsByFrame = collections.reduce((acc, c) => {
    if (c.frame_id) {
      (acc[c.frame_id] = acc[c.frame_id] || []).push(c);
    }
    return acc;
  }, {});

  // Get sample photos for preview (up to 5)
  const samplePhotos = (photos || []).slice(0, 5);

  const handleFileSelect = (file, type) => {
    if (!file) return;
    if (!file.type.includes('png')) {
      toast.error('Please upload a PNG file with transparency');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'landscape') {
        setLandscapeFile(file);
        setLandscapePreview(e.target.result);
      } else {
        setPortraitFile(file);
        setPortraitPreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file, type);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const resetForm = () => {
    setFrameName('');
    setLandscapeFile(null);
    setPortraitFile(null);
    setLandscapePreview(null);
    setPortraitPreview(null);
  };

  const handleCreate = async () => {
    if (!frameName.trim()) {
      toast.error('Please enter a frame name');
      return;
    }
    if (!landscapeFile || !portraitFile) {
      toast.error('Please upload both landscape and portrait overlays');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', frameName.trim());
      formData.append('landscape_overlay', landscapeFile);
      formData.append('portrait_overlay', portraitFile);

      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/frames`,
        formData,
        { ...axiosConfig, headers: { ...axiosConfig.headers, 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Frame created successfully');
      setShowCreateDialog(false);
      resetForm();
      onRefreshGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create frame');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!editingFrame) return;
    setSaving(true);
    try {
      const formData = new FormData();
      if (frameName.trim()) formData.append('name', frameName.trim());
      if (landscapeFile) formData.append('landscape_overlay', landscapeFile);
      if (portraitFile) formData.append('portrait_overlay', portraitFile);

      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/frames/${editingFrame.id}`,
        formData,
        { ...axiosConfig, headers: { ...axiosConfig.headers, 'Content-Type': 'multipart/form-data' } }
      );

      const overlaysChanged = !!landscapeFile || !!portraitFile;
      toast.success(overlaysChanged ? 'Frame updated — pre-renders cleared, ready to re-render' : 'Frame updated successfully');
      setShowEditDialog(false);
      setEditingFrame(null);
      resetForm();
      onRefreshGallery();
      if (overlaysChanged) {
        setRenderStatus(null);
        await fetchRenderStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update frame');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (frameId) => {
    setDeleting(frameId);
    try {
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/frames/${frameId}`,
        axiosConfig
      );
      toast.success('Frame deleted');
      onRefreshGallery();
    } catch (error) {
      toast.error('Failed to delete frame');
    } finally {
      setDeleting(null);
    }
  };

  const handleActivate = async (frameId) => {
    setActivating(frameId);
    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/frames/${frameId}/activate`,
        {},
        axiosConfig
      );
      toast.success('Frame activated');
      onRefreshGallery();
    } catch (error) {
      toast.error('Failed to activate frame');
    } finally {
      setActivating(null);
    }
  };

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/generate-branded-token`,
        {},
        axiosConfig
      );
      toast.success('Branded share link generated');
      onRefreshGallery();
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setGeneratingToken(false);
    }
  };

  // Pre-render functions
  const fetchRenderStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/frames/render-status`,
        axiosConfig
      );
      setRenderStatus(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  }, [backendUrl, galleryId, axiosConfig]);

  // Fetch render status on mount and when gallery changes
  useEffect(() => {
    fetchRenderStatus();
  }, [gallery]);

  // Poll while rendering
  useEffect(() => {
    if (renderStatus?.status === 'processing') {
      pollRef.current = setInterval(async () => {
        const status = await fetchRenderStatus();
        if (status && status.status !== 'processing') {
          clearInterval(pollRef.current);
          if (status.status === 'complete') {
            toast.success(`Pre-rendering complete! ${status.progress - (status.errors || 0)} photos rendered.`);
          }
        }
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [renderStatus?.status]);

  const handlePreRender = async () => {
    setRendering(true);
    try {
      const res = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/frames/pre-render`,
        {},
        axiosConfig
      );
      toast.success(res.data?.message || 'Pre-rendering started in background');
      await fetchRenderStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start pre-rendering');
    } finally {
      setRendering(false);
    }
  };

  const handleDeleteRenders = async () => {
    setDeletingRenders(true);
    try {
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/frames/renders`,
        axiosConfig
      );
      toast.success('Pre-rendered images deleted');
      setRenderStatus(null);
      onRefreshGallery();
    } catch (error) {
      toast.error('Failed to delete renders');
    } finally {
      setDeletingRenders(false);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const openEditDialog = (frame) => {
    setEditingFrame(frame);
    setFrameName(frame.name);
    setLandscapePreview(frame.landscape_overlay_url);
    setPortraitPreview(frame.portrait_overlay_url);
    setLandscapeFile(null);
    setPortraitFile(null);
    setShowEditDialog(true);
  };

  const handlePhotoLoad = (photoId, e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setPhotoOrientations(prev => ({
      ...prev,
      [photoId]: naturalWidth >= naturalHeight ? 'landscape' : 'portrait'
    }));
  };

  const OverlayUploadZone = ({ type, preview, inputRef }) => (
    <div
      className="relative border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center cursor-pointer hover:border-[#BF212F] transition-colors"
      onClick={() => inputRef.current?.click()}
      onDrop={(e) => handleDrop(e, type)}
      onDragOver={handleDragOver}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files[0], type)}
      />
      {preview ? (
        <div className="space-y-2">
          <div className="relative mx-auto bg-[repeating-conic-gradient(#d4d4d4_0%_25%,transparent_0%_50%)] bg-[length:16px_16px] rounded overflow-hidden" style={{ maxWidth: type === 'landscape' ? '200px' : '120px' }}>
            <img src={preview} alt={`${type} overlay`} className="w-full h-auto" />
          </div>
          <p className="text-xs text-slate-500">Click or drag to replace</p>
        </div>
      ) : (
        <div className="py-6">
          <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300 capitalize">{type} Overlay</p>
          <p className="text-xs text-slate-400 mt-1">PNG with transparency</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Frame className="w-6 h-6 text-[#BF212F]" />
            Photo Frames
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Add branded overlays to your event photos for sharing
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-[#BF212F] hover:bg-[#a51b27] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Frame
        </Button>
      </div>

      {/* Frame Cards */}
      {frames.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Frame className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-300">No frames yet</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-sm">
              Create a branded frame overlay to add to your event photos. Upload transparent PNG files for landscape and portrait orientations.
            </p>
            <Button
              onClick={() => { resetForm(); setShowCreateDialog(true); }}
              variant="outline"
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Frame
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {frames.map(frame => (
            <Card key={frame.id} className={`relative overflow-hidden ${frame.is_active ? 'ring-2 ring-[#BF212F]' : ''}`}>
              {frame.is_active && (
                <div className="absolute top-3 right-3 z-10">
                  <Badge className="bg-[#BF212F] text-white border-0">
                    <Check className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                </div>
              )}
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-slate-900 dark:text-white pr-20">{frame.name}</h3>

                {/* Overlay Thumbnails */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-slate-400 mb-1">Landscape</p>
                    <div className="bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:12px_12px] rounded border border-slate-200 dark:border-slate-700 overflow-hidden aspect-video">
                      <img src={frame.landscape_overlay_url} alt="Landscape" className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <div className="w-24">
                    <p className="text-xs text-slate-400 mb-1">Portrait</p>
                    <div className="bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:12px_12px] rounded border border-slate-200 dark:border-slate-700 overflow-hidden aspect-[3/4]">
                      <img src={frame.portrait_overlay_url} alt="Portrait" className="w-full h-full object-contain" />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-1">
                  {!frame.is_active && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleActivate(frame.id)}
                      disabled={activating === frame.id}
                    >
                      {activating === frame.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                      Set Active
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(frame)}
                  >
                    <Edit3 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(frame.id)}
                    disabled={deleting === frame.id}
                  >
                    {deleting === frame.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  </Button>
                </div>

                {/* Used-by chips */}
                {(collectionsByFrame[frame.id]?.length > 0 || frame.is_active) && (
                  <div className="flex flex-wrap items-center gap-1 pt-2 text-xs">
                    <span className="text-slate-500 dark:text-slate-400">Used by:</span>
                    {frame.is_active && (
                      <Badge variant="outline" className="text-xs border-[#BF212F]/40 text-[#BF212F]">
                        Gallery default
                      </Badge>
                    )}
                    {(collectionsByFrame[frame.id] || []).map(c => (
                      <Badge key={c.id} variant="secondary" className="text-xs">
                        {c.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Interactive Preview */}
      {activeFrame && samplePhotos.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Frame Preview</h3>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">
                  Photo {previewPhotoIndex + 1} of {samplePhotos.length}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewPhotoIndex(i => Math.max(0, i - 1))}
                    disabled={previewPhotoIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewPhotoIndex(i => Math.min(samplePhotos.length - 1, i + 1))}
                    disabled={previewPhotoIndex === samplePhotos.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="relative inline-block max-w-lg w-full">
                  <img
                    src={samplePhotos[previewPhotoIndex]?.preview_url || samplePhotos[previewPhotoIndex]?.thumbnail_url}
                    alt="Preview"
                    className="w-full h-auto rounded"
                    onLoad={(e) => handlePhotoLoad(samplePhotos[previewPhotoIndex]?.id, e)}
                  />
                  <img
                    src={
                      photoOrientations[samplePhotos[previewPhotoIndex]?.id] === 'portrait'
                        ? activeFrame.portrait_overlay_url
                        : activeFrame.landscape_overlay_url
                    }
                    alt="Frame overlay"
                    className="absolute inset-0 w-full h-full object-fill pointer-events-none"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Pre-Render Section */}
      {activeFrame && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Pre-Render Downloads</h3>
          <Card>
            <CardContent className="p-4">
              {renderStatus?.status === 'processing' ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-[#BF212F]" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Rendering... {renderStatus.progress || 0} / {renderStatus.total || 0} photos
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                    <div
                      className="bg-[#BF212F] h-2 rounded-full transition-all duration-500"
                      style={{ width: `${renderStatus.total ? (renderStatus.progress / renderStatus.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ) : renderStatus?.status === 'complete' && renderStatus?.frame_id === activeFrame?.id ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      {renderStatus.rendered_count} photos pre-rendered — downloads are instant
                    </span>
                  </div>
                  {renderStatus.errors > 0 && (
                    <p className="text-xs text-amber-600">{renderStatus.errors} photos had errors and will fall back to on-the-fly rendering</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handlePreRender}
                      disabled={rendering}
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Re-render
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleDeleteRenders}
                      disabled={deletingRenders}
                    >
                      {deletingRenders ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Delete Renders
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500">
                    Pre-render all photos with the active frame so downloads are instant — no processing wait time for guests.
                  </p>
                  <Button
                    onClick={handlePreRender}
                    disabled={rendering}
                    className="bg-[#BF212F] hover:bg-[#a51b27] text-white"
                  >
                    {rendering ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                    Pre-Render All Photos
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Branded Share Link */}
      {frames.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Branded Share Link</h3>
          <Card>
            <CardContent className="p-4">
              {!brandedToken ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-3">
                    Generate a share link for your branded gallery with framed photos
                  </p>
                  <Button
                    onClick={handleGenerateToken}
                    disabled={generatingToken}
                    className="bg-[#BF212F] hover:bg-[#a51b27] text-white"
                  >
                    {generatingToken && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Generate Branded Link
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                    <span className="text-sm text-slate-600 dark:text-slate-300 truncate flex-1 font-mono">
                      {brandedUrl}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(brandedUrl)}
                    >
                      {copiedLink ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
                      {copiedLink ? 'Copied' : 'Copy Link'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowQRModal(true)}
                    >
                      <QrCode className="w-4 h-4 mr-1" />
                      QR Code
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(brandedUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open
                    </Button>
                  </div>
                  {!activeFrame && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      No active frame set. Visitors will see photos without frames until you activate one.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Frame Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Frame className="w-5 h-5 text-[#BF212F]" />
              Create New Frame
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Frame Name</Label>
              <Input
                value={frameName}
                onChange={(e) => setFrameName(e.target.value)}
                placeholder="e.g. Gold Gala Frame"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Landscape Overlay</Label>
                <OverlayUploadZone type="landscape" preview={landscapePreview} inputRef={landscapeInputRef} />
              </div>
              <div>
                <Label className="mb-2 block">Portrait Overlay</Label>
                <OverlayUploadZone type="portrait" preview={portraitPreview} inputRef={portraitInputRef} />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Upload transparent PNG files. The overlay will be stretched to fit each photo's dimensions.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-[#BF212F] hover:bg-[#a51b27] text-white"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Frame
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Frame Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-[#BF212F]" />
              Edit Frame
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Frame Name</Label>
              <Input
                value={frameName}
                onChange={(e) => setFrameName(e.target.value)}
                placeholder="Frame name"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Landscape Overlay</Label>
                <OverlayUploadZone type="landscape" preview={landscapePreview} inputRef={landscapeInputRef} />
              </div>
              <div>
                <Label className="mb-2 block">Portrait Overlay</Label>
                <OverlayUploadZone type="portrait" preview={portraitPreview} inputRef={portraitInputRef} />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Only upload new overlays if you want to replace the existing ones.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button
              onClick={handleEdit}
              disabled={saving}
              className="bg-[#BF212F] hover:bg-[#a51b27] text-white"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal}
        onClose={() => setShowQRModal(false)}
        galleryName={gallery?.name}
        url={brandedUrl}
        title="Branded Gallery"
      />
    </div>
  );
};

export default FramesPanel;
